<template>
    <IntroSplash 
        :is-ready="isAppReady" 
        @hidden="onSplashHidden" 
    />
    <router-view />
    <PushPermissionBanner />
    <TooltipProvider />
</template>

<script setup>
    import { ref, watch, onMounted, onUnmounted, onBeforeUnmount } from "vue";
    import { useUserStore } from "@/stores/userStore";
    import { useDashboardStore } from "@/stores/dashboardStore";
    import { wsService } from "@/services/websocket";
    import { useRouter } from 'vue-router';
    import { App as CapacitorApp } from '@capacitor/app';
    import { Capacitor } from '@capacitor/core';
    import { usePush } from "@/composables/usePush";
    import TooltipProvider from '@/components/elements/TooltipProvider.vue';
    import IntroSplash from "./components/elements/IntroSplash.vue";
    import PushPermissionBanner from '@/components/modals/PushPermissionBanner.vue';

    const router = useRouter();
    let backListener = null;

    // 1. Инициализация сторов и реактивных переменных
    const userStore = useUserStore();
    const dashboardStore = useDashboardStore();
    const isAppReady = ref(false);
    const { initPush } = usePush();
    let appUrlOpenListener = null;

    const openNotificationRoute = (url) => {
        try {
            const target = new URL(url).searchParams.get('route');
            if (target && target.startsWith('/') && !target.startsWith('//')) {
                router.push(target);
            }
        } catch (error) {
            console.warn('[push] invalid notification URL:', error);
        }
    };

    // 2. Логика WebSocket
    const connectWebSocket = () => {
        if (userStore.isAuthenticated && !wsService.isConnected.value) {
            console.log("[App] Connecting WebSocket...");
            wsService.connect();
        }
    };

    const disconnectWebSocket = () => {
        if (wsService.isConnected.value) {
            console.log("[App] Disconnecting WebSocket...");
            wsService.disconnect();
        }
    };

    const unwatchAuth = watch(
        () => userStore.isAuthenticated,
        async (isAuth) => {
            if (isAuth) {
                connectWebSocket();

                if (Capacitor.isNativePlatform()) {
                    await initPush();
                }                
            } else {
                disconnectWebSocket();
                dashboardStore.reset();
            }
        },
        { immediate: true }
    );

    setTimeout(() => {
        isAppReady.value = true; 
    }, 3500);

    const onSplashHidden = () => {
        // console.log('Заставка полностью удалена из DOM');
    };

    onMounted(async () => {
        if (Capacitor.isNativePlatform()) {
            backListener = await CapacitorApp.addListener('backButton', async ({ canGoBack }) => {
                if (canGoBack || router.currentRoute.value.path !== '/') {
                    router.back();
                } else {
                    CapacitorApp.minimizeApp();
                }
            });

            appUrlOpenListener = await CapacitorApp.addListener('appUrlOpen', ({ url }) => {
                openNotificationRoute(url);
            });

            const launchUrl = await CapacitorApp.getLaunchUrl();
            if (launchUrl?.url) openNotificationRoute(launchUrl.url);
        }
    });

    onBeforeUnmount(() => {
        unwatchAuth();
        disconnectWebSocket();
    });

    onUnmounted(() => {
        if (backListener) {
            backListener.remove();
        }
        if (appUrlOpenListener) {
            appUrlOpenListener.remove();
        }
    });    
</script>
